import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Send, CheckCircle2, MessageSquareWarning, EyeOff, Trash2, ExternalLink, Loader2, Info } from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { Card, Loading } from "../ui";
import { StatusBadge, itemStatus, Modal, useFlash, errText, notifyCmsChanged, Label } from "./cmsShared";
import { schemaFor, PUBLIC_PATH } from "./schemas";
import { Field, ItemsEditor } from "./FieldEditors";
import { AuditTrail, Preview } from "./EditorSide";
import VersionHistory from "./VersionHistory";
import { useCmsBase } from "./cmsBase";

export default function ContentEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const base = useCmsBase();
  const { user } = useAuth();
  const caps = user?.cms || {};
  const [item, setItem] = useState(null);
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const [flashNode, flash] = useFlash();

  const apply = useCallback((it) => {
    setItem(it);
    setData(JSON.parse(JSON.stringify(it.draft?.data || it.published?.data || {})));
    setDirty(false);
  }, []);
  useEffect(() => {
    let alive = true;
    api.get(`/cms/content/${id}`).then(({ data: d }) => alive && apply(d));
    return () => { alive = false; };
  }, [id, apply]);

  if (!item || !data) return <Loading />;
  const schema = schemaFor(item);
  const status = itemStatus(item);
  const readOnly = !caps.author;
  const update = (patch) => { setData((d) => ({ ...d, ...patch })); setDirty(true); };

  const act = async (name, fn, msg) => {
    setBusy(name);
    try {
      const res = await fn();
      if (res?.data?.id) apply(res.data);
      notifyCmsChanged();
      flash(msg);
      return res;
    } catch (e) { flash(errText(e), "error"); }
    finally { setBusy(""); }
  };
  const saveDraft = () => api.post(`/cms/content/${id}/draft`, { data });
  const onSave = () => act("save", saveDraft, "Draft saved — the live site is unchanged.");
  const onSubmit = () => act("submit", async () => { if (dirty || !item.draft) await saveDraft(); return api.post(`/cms/content/${id}/submit`); }, "Submitted for review.");
  const onApprove = () => act("approve", () => api.post(`/cms/content/${id}/approve`), "Approved & published — now live.");
  const onRequest = async () => { setNoteOpen(false); await act("changes", () => api.post(`/cms/content/${id}/request-changes`, { note }), "Changes requested."); setNote(""); };
  const onUnpublish = () => window.confirm("Remove this article from the public site?") && act("unpublish", () => api.post(`/cms/content/${id}/unpublish`), "Unpublished.");
  const onDelete = async () => {
    if (!window.confirm("Delete this article permanently?")) return;
    await act("delete", () => api.delete(`/cms/content/${id}`), "Deleted.");
    nav(`${base}/content`);
  };
  const Spin = ({ n, icon: I }) => (busy === n ? <Loader2 size={15} className="animate-spin" /> : <I size={15} />);

  return (
    <div data-testid="cms-editor">
      <Link to={`${base}/content`} onClick={(e) => dirty && !window.confirm("Discard unsaved changes?") && e.preventDefault()} className="mb-6 inline-flex items-center gap-2 font-sans text-xs uppercase tracking-widest text-sapphire-deep/60 hover:text-gold-deep" data-testid="cms-back">
        <ArrowLeft size={14} /> All content
      </Link>
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <h1 className="font-display text-4xl font-semibold text-sapphire-deep" data-testid="cms-editor-title">{item.label}</h1>
        <StatusBadge status={status} testid="cms-editor-status" />
        {dirty && <span className="font-sans text-xs italic text-gold-deep" data-testid="cms-unsaved">Unsaved changes</span>}
      </div>

      {item.published && !item.draft && caps.author && (
        <p className="mb-6 flex items-center gap-2 rounded-xl bg-emerald/8 px-4 py-3 font-sans text-sm text-emerald" data-testid="cms-live-notice">
          <Info size={16} /> You are editing the live version. Saving creates a new draft — the website stays unchanged until it is approved.
        </p>
      )}
      {status === "in_review" && item.draft?.restored_from && (
        <p className="mb-6 rounded-xl border border-gold/30 bg-gold/10 px-5 py-3 font-sans text-sm text-gold-deep" data-testid="cms-restore-notice">
          Rollback request: {item.draft.submitted_by_name} wants to restore version {item.draft.restored_from}. Compare it under Version history before approving.
        </p>
      )}
      {status === "changes_requested" && item.draft?.note && (
        <div className="mb-6 rounded-xl border border-garnet/20 bg-garnet/5 px-5 py-4" data-testid="cms-reviewer-note">
          <p className="font-sans text-xs font-semibold uppercase tracking-widest text-garnet">Reviewer note · {item.draft.reviewed_by_name}</p>
          <p className="mt-1 font-sans text-sm text-charcoal">{item.draft.note}</p>
        </div>
      )}

      <div className="grid gap-8 xl:grid-cols-[1fr_360px]">
        <Card>
          {schema?.items ? (
            <ItemsEditor spec={schema.items} items={data.items} onChange={(items) => update({ items })} readOnly={readOnly} />
          ) : (
            <div className="grid gap-5">
              {(schema?.fields || []).map((f) => (
                <Field key={f.k} f={f} value={data[f.k]} onChange={(v) => update({ [f.k]: v })} readOnly={readOnly} tid={`field-${f.k}`} />
              ))}
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card className="space-y-3" data-testid="cms-actions">
            <Label>Workflow</Label>
            {caps.author && (
              <>
                <button onClick={onSave} disabled={!!busy || !dirty} className="btn-dark w-full" data-testid="cms-save-draft"><Spin n="save" icon={Save} /> Save draft</button>
                <button onClick={onSubmit} disabled={!!busy || (status === "in_review" && !dirty) || (status === "published" && !dirty)} className="btn-sapphire w-full" data-testid="cms-submit-review"><Spin n="submit" icon={Send} /> Submit for review</button>
              </>
            )}
            {caps.reviewer && status === "in_review" && (
              <>
                <button onClick={onApprove} disabled={!!busy || dirty} className="btn-primary w-full" data-testid="cms-approve"><Spin n="approve" icon={CheckCircle2} /> Approve & publish</button>
                <button onClick={() => setNoteOpen(true)} disabled={!!busy} className="btn-dark w-full !border-garnet !text-garnet hover:!bg-garnet hover:!text-parchment" data-testid="cms-request-changes"><MessageSquareWarning size={15} /> Request changes</button>
              </>
            )}
            {caps.admin && item.type === "article" && item.published && (
              <button onClick={onUnpublish} disabled={!!busy} className="flex w-full items-center justify-center gap-2 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-stonegrey hover:text-garnet" data-testid="cms-unpublish"><EyeOff size={14} /> Unpublish</button>
            )}
            {caps.admin && item.type === "article" && (
              <button onClick={onDelete} disabled={!!busy} className="flex w-full items-center justify-center gap-2 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-garnet/70 hover:text-garnet" data-testid="cms-delete"><Trash2 size={14} /> Delete article</button>
            )}
            {item.published && (
              <a href={item.type === "article" ? `/news/${item.key}` : PUBLIC_PATH[item.section]} target="_blank" rel="noreferrer" className="link-underline text-sm" data-testid="cms-view-live">View live page <ExternalLink size={13} /></a>
            )}
          </Card>
          <Card><AuditTrail item={item} /></Card>
          <Card><VersionHistory item={item} caps={caps} flash={flash} onRestored={apply} /></Card>
          <Card><Label>Preview</Label><Preview item={item} data={data} /></Card>
        </div>
      </div>

      {noteOpen && (
        <Modal title="Request changes" onClose={() => setNoteOpen(false)} testid="changes-modal">
          <textarea rows={4} className="input-lodge resize-none" placeholder="What should the author change?" value={note} onChange={(e) => setNote(e.target.value)} data-testid="changes-note" />
          <div className="mt-5 flex justify-end gap-3">
            <button onClick={() => setNoteOpen(false)} className="btn-dark">Cancel</button>
            <button onClick={onRequest} disabled={!note.trim()} className="btn-sapphire" data-testid="changes-send">Send back to author</button>
          </div>
        </Modal>
      )}
      {flashNode}
    </div>
  );
}
